import React from "react";
import { Link } from "react-router-dom";
import { Heart, Star, ShieldCheck, MapPin } from "lucide-react";
import VerifiedRibbon from "./VerifiedRibbon";
import { useLang } from "@/contexts/LangContext";
import { profileMainImage, onImageError } from "@/lib/img";

/**
 * ProfileCard — used in Directory, Home featured, Agency details.
 *
 * Layout:
 *   • Mobile (<640px): COMPACT — 3:4 photo, name+age, city, badges, price-from
 *     overlaid; trust/rating hidden; bottom price strip compressed.
 *   • ≥sm: full luxury card with rating + trust + spacious price strip.
 */
export default function ProfileCard({ profile, isFavorite, onFavorite }) {
  const { t } = useLang();
  const verified = profile.verification_level === "verified" ||
                   profile.verification_status === "verified";
  const imgSrc = profileMainImage(profile);
  // Split pricing surfaces. The card renders whichever the profile has set —
  // both, either, or falls back to the legacy `price_from` if neither exists
  // (so old profiles keep displaying a price while owners migrate).
  const incall = profile.incall_price;
  const outcall = profile.outcall_price;
  const hasIncall = typeof incall === "number" && incall > 0;
  const hasOutcall = typeof outcall === "number" && outcall > 0;
  const legacyFrom = profile.price_from;

  return (
    <div className="card-luxe rounded-md sm:rounded-lg overflow-hidden group relative animate-fade-up opacity-0" data-testid={`profile-card-${profile.id}`}>
      <Link to={`/profile/${profile.id}`} className="block relative aspect-[3/4] overflow-hidden">
        <img
          src={imgSrc}
          alt={profile.name}
          onError={onImageError}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-black/30" />

        {/* Verified — diagonal corner ribbon in top-right (kept SEPARATE from VIP/TOP) */}
        {verified && <VerifiedRibbon data-testid={`ribbon-${profile.id}`} />}

        {/* VIP / TOP / paid-placement badges — top-left stack, deliberately
            distinct from the verification ribbon so identity vs paid-tier
            signals remain visually independent. Mobile sizing bumped ~+12%
            for legibility on 3:4 thumbnails; desktop unchanged. */}
        <div className="absolute top-2 left-2 sm:top-3 sm:left-3 flex flex-col gap-1">
          {profile.top_until && (
            <span className="inline-flex items-center gap-1 px-2 py-1 sm:px-2.5 sm:py-1 rounded-full text-[10.5px] sm:text-[10px] font-medium bg-gold/20 backdrop-blur-md text-gold border border-gold/40">
              ★ TOP
            </span>
          )}
          {profile.vip_until && !profile.top_until && (
            <span className="inline-flex items-center gap-1 px-2 py-1 sm:px-2.5 sm:py-1 rounded-full text-[10.5px] sm:text-[10px] font-medium bg-gold/20 backdrop-blur-md text-gold border border-gold/40">
              VIP
            </span>
          )}
        </div>

        {/* Favourite — moved to bottom-right so the top-right ribbon has full corner. */}
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onFavorite?.(profile); }}
          className="hidden sm:flex absolute bottom-3 right-3 w-8 h-8 rounded-full bg-black/60 backdrop-blur-md border border-white/10 items-center justify-center hover:border-gold/50 transition-colors z-10"
          data-testid={`favorite-btn-${profile.id}`}
        >
          <Heart className={`w-4 h-4 ${isFavorite ? "fill-gold text-gold" : "text-white"}`} strokeWidth={1.5} />
        </button>

        {/* Bottom overlay — compact on mobile, full on desktop */}
        <div className="absolute bottom-0 left-0 right-0 p-2 sm:p-4">
          {/* Mobile compact block — name / city / incall+outcall prices */}
          <div className="sm:hidden">
            <p className="font-heading text-sm text-white leading-tight truncate">
              {profile.name}<span className="text-[#d4d4d4] font-light">, {profile.age}</span>
            </p>
            <p className="text-[10px] text-[#d4d4d4] truncate flex items-center gap-1 mt-0.5">
              <MapPin className="w-2.5 h-2.5 flex-none" strokeWidth={1.5} />{profile.city}
            </p>
            {(hasIncall || hasOutcall) ? (
              <p className="text-[11px] text-gold leading-tight mt-0.5" data-testid={`mobile-price-${profile.id}`}>
                {hasIncall && <span>In €{incall}</span>}
                {hasIncall && hasOutcall && <span className="text-[#737373] mx-1">·</span>}
                {hasOutcall && <span>Out €{outcall}</span>}
              </p>
            ) : legacyFrom ? (
              <p className="text-[11px] text-gold leading-tight mt-0.5" data-testid={`mobile-price-${profile.id}`}>
                {t.profile.from} €{legacyFrom}
              </p>
            ) : null}
          </div>
          {/* Desktop full block */}
          <div className="hidden sm:flex items-end justify-between">
            <div>
              <p className="font-heading text-xl text-white tracking-wide">{profile.name}, <span className="text-[#d4d4d4]">{profile.age}</span></p>
              <div className="flex items-center gap-2 text-xs text-[#d4d4d4] mt-0.5">
                <MapPin className="w-3 h-3" strokeWidth={1.5} />
                {profile.city}
                <span className="text-[#737373]">•</span>
                <span className="text-gold uppercase tracking-wider text-[10px]">
                  {profile.is_independent ? t.profile.independent : t.profile.agency}
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-1 justify-end text-xs text-gold">
                <Star className="w-3.5 h-3.5 fill-gold" strokeWidth={0} /> {profile.rating?.toFixed(1) || "—"}
              </div>
              <div className="flex items-center gap-1 justify-end text-[10px] text-emerald-300 mt-0.5">
                <ShieldCheck className="w-3 h-3" strokeWidth={1.5} /> {profile.trust_score}
              </div>
            </div>
          </div>
        </div>
      </Link>

      {/* Desktop-only bottom price strip — shows In / Out when set, falls
          back to legacy `from` price for older profiles. */}
      <div className="hidden sm:flex px-4 py-3 items-center justify-between border-t border-white/5" data-testid={`desktop-price-${profile.id}`}>
        {(hasIncall || hasOutcall) ? (
          <>
            <div className="flex items-center gap-3">
              {hasIncall && (
                <div className="flex items-baseline gap-1.5">
                  <span className="text-[10px] uppercase tracking-wider text-[#737373]">In</span>
                  <span className="font-heading text-base text-gold">€{incall}</span>
                </div>
              )}
              {hasOutcall && (
                <div className="flex items-baseline gap-1.5">
                  <span className="text-[10px] uppercase tracking-wider text-[#737373]">Out</span>
                  <span className="font-heading text-base text-gold">€{outcall}</span>
                </div>
              )}
            </div>
            <span className="text-[10px] uppercase tracking-wider text-[#737373]">{profile.currency || "EUR"}</span>
          </>
        ) : (
          <>
            <span className="text-xs text-[#737373] uppercase tracking-wider">{t.profile.from}</span>
            <span className="font-heading text-lg text-gold">€{legacyFrom || "—"}</span>
          </>
        )}
      </div>
    </div>
  );
}
